import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { login } from '../../api/auth';
import { ApiError } from '../../api/client';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { consumeReturnTo, sanitizeReturnTo } from '../../lib/return-to';

export const DEFAULT_AFTER_LOGIN = '/inbox';

function loginErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Не удалось войти. Проверьте соединение и попробуйте ещё раз.';
  if (err.code === 'invalid_credentials' || err.code === 'invalid_body') return 'Неверный email или пароль';
  if (err.code === 'too_many_attempts' || err.code === 'account_disabled') return err.message;
  if (err.status === 429) return 'Слишком много попыток входа. Подождите минуту и попробуйте ещё раз.';
  return 'Не удалось войти. Попробуйте ещё раз.';
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await login(email.trim(), password);
      // Back to where the visitor was heading (e.g. the extension's connect link).
      // Both sources are untrusted input and pass the same in-app-path filter.
      const target = consumeReturnTo() ?? sanitizeReturnTo(params.get('next')) ?? DEFAULT_AFTER_LOGIN;
      void navigate(target, { replace: true });
    } catch (err) {
      setError(loginErrorText(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-muted/40">
      <form onSubmit={(e) => void handleSubmit(e)} className="w-80 rounded border border-border bg-card p-6 shadow-sm">
        <h1 className="mb-4 text-lg font-semibold">Вход в Outreach Desk</h1>
        <label className="mb-3 block text-sm">
          Email
          <Input
            className="mt-1"
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="mb-4 block text-sm">
          Пароль
          <Input
            className="mt-1"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && (
          <p role="alert" className="mb-3 text-sm text-red-600">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={pending}>
          {pending ? 'Входим…' : 'Войти'}
        </Button>
        <p className="mt-4 text-xs text-muted-fg">
          Учётную запись создаёт владелец рабочего пространства. Забыли пароль — попросите его выдать новый.
        </p>
      </form>
    </div>
  );
}
